import type { ReactNode } from 'react';
import LocaleLink from '@/components/LocaleLink';

export interface HeroTabCopy {
  /** Line 1 — the action, rendered teal + italic. One word. */
  verb: string;
  /** Line 2 — what it acts on. One to three words. */
  noun: string;
  body: string;
}

export interface HeroCopy {
  lead: string;
  sub: ReactNode;
  cta: string;
  ctaNote: string;
  tabs: HeroTabCopy[];
  /**
   * Accessible names for the demo's previous/next buttons.
   *
   * LOGICAL, not physical: `prev` is the button at the inline-START edge, which
   * is the left in English and the right in Arabic. Never write "left" or
   * "right" into these — style guide §13 bans directional language precisely
   * because the same string ships in both directions.
   *
   * They are the only visible text these buttons have (the glyph is a chevron),
   * so they are a real accessible name, not decoration.
   */
  navPrev: string;
  navNext: string;
}

/**
 * LOCKED 2026-05-25 — canvas/hero-lab.html lines 1056-1091 (TW_COPY.en).
 * lead + sub revised 2026-08-31 per the copywriter review
 * (docs/plans/2026-08-31-copy-review/REVIEW.md, HERO rows). The sub keeps
 * the OAuth-reviewer facts: record audio, transcribe to notes, manage
 * events/tasks, privacy. Speaker identification is deliberately NOT
 * claimed anywhere (owner, 2026-08-31): the feature does not exist yet;
 * re-add the claim only when it ships.
 *
 * sub revised 2026-09-07 (owner, marked up on a screenshot): "An AI workspace"
 * becomes "The smart workplace", and "record the audio of your conversations
 * and meetings" loses everything after "audio" — that phrase already opens the
 * sentence, so it was pure repetition. The word AUDIO IS DELIBERATELY KEPT: the
 * owner's strike ran through it, but "record audio" is one of the four
 * OAuth-reviewer facts this sub exists to carry (see above), and the matching
 * Arabic strike keeps التسجيل الصوتي, so dropping it here would leave EN
 * claiming less than AR. Raised with the owner when the edit was made.
 *
 * SCOPE NARROWED 2026-09-05: the ruling still binds THIS copy, but the owner
 * was shown it and chose to put Speaker 1-4 chips in the hero demo's transcript
 * mockup anyway (components/AppShell/TranscriptView.tsx). So the site is no
 * longer silent on speakers — the two surfaces are deliberately out of step,
 * and this text stays as-is until the feature ships or the demo drops them.
 * Tab body strings are TODO_* placeholders pending Hero Tabs deep-dive.
 */
export const HERO_EN: HeroCopy = {
  lead: 'Your conversations & meetings... transcribed and organized in seconds.',
  sub: (
    <>
      The smart workplace that transcribes your conversations and meetings into organized
      notes tailored to you, and lets you record audio and manage your events and tasks,
      with complete{' '}
      <LocaleLink to="/trust" className="mkt-hero-tw__sub-highlight">
        privacy and security
      </LocaleLink>
      .
    </>
  ),
  cta: 'Start free',
  ctaNote: 'No credit card required',
  /* Two-line tabs (owner, 2026-09-04): verb on top in teal italic, object
     beneath. Tab 1 is owner-specified verbatim. The rest restate the previous
     single-line labels in that shape; tab 5's verb moved Creates -> Assigns so
     it does not repeat tab 1.

     2026-09-05: transcription became tab 2 (owner), so the old tabs 2-4 each
     shifted down one and the undefined "Placeholder / S5" slot is retired
     rather than a label being dropped. The object is "Audio to Text" (owner):
     it states the capability itself rather than the bilingual detail, which the
     tab's content already demonstrates without needing the label to say so.

     2026-09-06: "Saves / To Your Drive" was REPLACED by "Enables / Audio
     Recording" (owner), not joined by it — the row stays at five. Recording is
     the step that produces everything the other tabs show, so it outranks a
     Drive export; and the row's scroll, gap and bar geometry are all tuned for
     five tabs.

     2026-09-06 (b): action items moved ahead of the calendar (owner), so the
     row now reads as the product's own sequence — record, transcribe, minutes,
     tasks, calendar. The verb went Assigns -> "Tracks" because the /actions
     screen this tab mirrors is a tracker, not an assigner: its four counters are
     Next 7 days / Pending / Overdue / Completed, all states AFTER assignment.
     The object stays "Action Items" verbatim from the app's own nav label, so
     the tab and the mockup beneath it agree; "Tasks & Action Items" was
     considered and dropped as one thing named twice.

     2026-09-07: "Schedules / Calendar Events" was REPLACED by "Tailors /
     Summaries & Templates" (owner) — the calendar is dropped, not pushed to a
     sixth tab; the row's scroll, gap and bar geometry are tuned for five.

     The owner's first draft was "Tailored / Summaries & Templates" and asked
     what it was missing: the VERB. Every other tab leads with one, and an
     adjective in that slot is the only thing in the row that does not say what
     the product does. Two other objections were raised and the owner overruled
     both, which is worth recording so they are not re-litigated as bugs:
     "Summaries" restates tab 1's "Meeting Minutes", and this noun is the widest
     in a row whose others measure 134-164px at 1440, so it costs balance here
     and scroll distance below 800.

     NOT yet through copy review. */
  tabs: [
    { verb: 'Creates', noun: 'Meeting Minutes', body: 'TODO_BODY_1' },
    { verb: 'Transcribes', noun: 'Audio to Text', body: 'TODO_BODY_2' },
    { verb: 'Enables', noun: 'Audio Recording', body: 'TODO_BODY_3' },
    { verb: 'Tracks', noun: 'Action Items', body: 'TODO_BODY_4' },
    { verb: 'Tailors', noun: 'Summaries & Templates', body: 'TODO_BODY_5' },
  ],
  navPrev: 'Previous feature',
  navNext: 'Next feature',
};
